<template>
  <section
    ref="carousel"
    :class="background"
    class="py-12 md:py-16"
  >
    <h2
      class="text-center mx-8 md:mx-auto uppercase font-druk text-display-10 md:text-display-14 tracking-02-em leading-10 md:leading-14 mb-6 md:mb-12"
      :class="textClass"
    >
      {{ title }}
    </h2>
    <MajorityUiMajorityCarousel
      class="container"
      scene-class="mx-4"
      slide-gap="2rem"
      per-page-md="1"
      per-page-lg="4"
      pagination-class="hidden lg:flex lg:mt-8"
      :preview-percentage-lg="0"
      :preview-percentage-md="67"
      :preview-percentage="15"
      show-preview
      :class="textClass"
      :light-pagination="isPaginationLight"
    >
      <article
        v-for="(item, index) in items"
        :key="index"
        class="w-full relative"
      >
        <MajorityUiMajorityLink
          class="absolute w-full h-full z-10"
          tracking-name="communityCarouselBlock"
          tracking-type="community content"
          :to="getTo(item)"
        />
        <figure
          class="select-none relative mb-4 lg:mb-2 bg-middle-light ar-square"
        >
          <div
            v-if="hasTag(item)"
            class="tag uppercase absolute font-medium tracking-01-em leading-4 md:leading-5 text-tiny md:text-smaller bg-bright-yellow py-px md:py-2px px-half-space text-dark z-10"
          >
            <ul>
              <li
                v-for="(tag, index2) in getTag(item)"
                :key="index2"
              >
                {{ tag }}
              </li>
            </ul>
          </div>
          <MajorityUiMajorityImage
            :src="item.imageUrl"
            :alt="item.title"
            class="object-cover w-full h-full absolute inset-0"
            fade-in
            lazy
            root-margin="100px 100vw 100px 100vw"
            :optmize-options="{ w: 640 }"
          />
        </figure>
        <h3
          class="font-bold tracking-02-em text-smaller leading-6 mb-2"
        >
          {{ item.title }}
        </h3>
        <p
          class="font-light text-smallest leading-5 mb-0"
        >
          {{ item.description }}
        </p>
      </article>
    </MajorityUiMajorityCarousel>
    <div class="text-center">
      <MajorityUiMajorityLink
        v-if="hasSeeAll"
        class="btn mt-8"
        :class="buttonClass"
        tracking-name="communityCarouselBlockSeeAll"
        tracking-type="community content"
        :to="seeAllTo"
        :text="$messages('label.see_all')"
      />
    </div>
  </section>
</template>

<script>
import { isEmptyOrWhiteSpace, isNullOrUndefined } from '@/utils/comparators'
import { BRAND_BACKGROUND_COLORS } from '@/utils/constants'
import { isDarkOrLightByElement } from '@/utils/contrast-calculator'
export default {
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
      validator: val => val.every(item => !isNullOrUndefined(item.slug) && !isNullOrUndefined(item.type) && !isNullOrUndefined(item) && !isNullOrUndefined(item.title) && !isNullOrUndefined(item.description) && !isNullOrUndefined(item.imageUrl))
    },
    title: {
      type: String,
      required: true,
      default: ''
    },
    seeAll: {
      type: Object,
      default: null,
      validator: val => !isNullOrUndefined(val.type)
    },
    background: {
      type: String,
      default: 'bg-light-green',
      validator: val => BRAND_BACKGROUND_COLORS.includes(val)
    }
  },
  data () {
    return {
      calculatedTextVariant: 'dark'
    }
  },
  computed: {
    buttonClass () {
      return this.calculatedTextVariant === 'light'
        ? 'btn-bright-yellow'
        : 'btn-infra-violette'
    },
    textClass () {
      return this.calculatedTextVariant === 'light'
        ? 'text-white'
        : 'text-dark'
    },
    isPaginationLight () {
      return this.calculatedTextVariant === 'light'
    },
    hasSeeAll () {
      return this.seeAllTo !== ''
    },
    seeAllTo () {
      let link = ''
      if (this.seeAll === null) {
        link = ''
        return link
      }

      if (this.seeAll.type === 'article') {
        link = this.$mRouter.getLocalRoute(this.seeAll.slug ? { name: 'community-slug', params: { slug: this.seeAll.slug } } : { name: 'community-slug', params: { slug: 'articles' } })
      }

      if (this.seeAll.type === 'event') {
        link = this.$mRouter.getLocalRoute({ name: 'community-slug', params: { slug: 'events' } })
      }

      if (this.seeAll.type === 'discount') {
        link = this.$mRouter.getLocalRoute({ name: 'visa-debit-card-discounts-explore-discounts' })
      }

      return link
    }
  },
  mounted () {
    this.calculateTextVariant()
  },
  methods: {
    calculateTextVariant () {
      try {
        this.calculatedTextVariant = isDarkOrLightByElement(this.$refs.carousel)
      } catch (error) {
        this.calculatedTextVariant = 'dark'
      }
    },
    hasTag (item) {
      return !isNullOrUndefined(item.tag) && !isEmptyOrWhiteSpace(item.tag.name)
    },
    getTag (item) {
      if (!this.hasTag(item)) {
        return []
      }

      const lengthLimit = 16
      const splittedTag = ['']

      item.tag.name.split(' ').forEach((word) => {
        const index = splittedTag.length - 1

        if (splittedTag[index].length + word.length >= lengthLimit) {
          splittedTag.push(word)
        } else {
          splittedTag[index] = `${splittedTag[index]} ${word}`
        }
      })

      return splittedTag
    },
    getTo (item) {
      if (item.type === 'event') {
        return this.$mRouter.getLocalRoute({ name: 'community-event-slug', params: { slug: item.slug } })
      }

      if (item.type === 'discount') {
        return this.$mRouter.getLocalRoute({ name: 'community-discount-slug', params: { slug: item.slug } })
      }

      return this.$mRouter.getLocalRoute({ name: 'community-article-slug', params: { slug: item.slug } })
    }
  }
}
</script>

<style lang="postcss" scoped>
.tag {
  top: min(7%, 16px);
  right: min(7%, 16px);

  @screen md {
    top: min(10%, 32px);
    right: min(10%, 32px);
  }
}
</style>
